/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger } from "matrix-js-sdk/lib/logger";
import { useCallback, useRef } from "react";
import { DisconnectReason } from "livekit-client";

import { useSubmitRageshake } from "../settings/submit-rageshake";
import { ElementCallError, ErrorCode } from "../utils/errors";

const RECONNECT_WINDOW_MS = 300_000;
const RECONNECT_THRESHOLD = 2;
const INCIDENT_DEDUPE_MS = 10_000;
const AUTO_RAGESHAKE_METADATA_PREFIX = "AutoRageshakeMeta:";

export type CallRageshakeClassification = "handled" | "unhandled";

export interface CallRageshakeIncident {
  classification: CallRageshakeClassification;
  reason: string;
  screen: string;
  trigger?: string;
  details?: Record<string, unknown>;
}

export interface CallRageshakeErrorContext {
  screen: string;
  details?: Record<string, unknown>;
}

interface ClassifiedCallError {
  classification: CallRageshakeClassification;
  reason: string;
  trigger: string;
  details: Record<string, unknown>;
}

function normalizeUnknownError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    const cause =
      error.cause instanceof Error
        ? {
            name: error.cause.name,
            message: error.cause.message,
            stack: error.cause.stack,
          }
        : error.cause;

    return {
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
      errorCause: cause,
    };
  }

  return {
    errorValue: error,
  };
}

function detailsReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Error) {
    return normalizeUnknownError(value);
  }

  if (typeof value === "bigint") {
    return value.toString();
  }

  return value;
}

function formatClassification(
  classification: CallRageshakeClassification,
): string {
  return classification === "unhandled"
    ? "необработанная ошибка"
    : "обработанный кейс";
}

export function shouldAutoReportLivekitDisconnect(
  reason?: DisconnectReason,
): boolean {
  return (
    reason !== DisconnectReason.CLIENT_INITIATED &&
    reason !== DisconnectReason.USER_REJECTED
  );
}

function buildDefaultIncidentTrigger(
  incident: Pick<
    CallRageshakeIncident,
    "classification" | "reason" | "details"
  >,
): string {
  if (incident.reason === "reconnect_threshold") {
    const reconnectCountInWindow =
      typeof incident.details?.reconnectCountInWindow === "number"
        ? incident.details.reconnectCountInWindow
        : RECONNECT_THRESHOLD;
    const reconnectWindowMs =
      typeof incident.details?.reconnectWindowMs === "number"
        ? incident.details.reconnectWindowMs
        : RECONNECT_WINDOW_MS;

    return `За ${Math.round(
      reconnectWindowMs / 1000,
    )} секунд произошло не меньше ${reconnectCountInWindow} реконнектов.`;
  }

  if (incident.reason === "membership_manager_error") {
    return "Код обработал ошибку membership manager и перевёл звонок в сценарий потери соединения.";
  }

  if (
    incident.reason === "livekit_disconnected" ||
    incident.reason === "livekit_unexpected_disconnect"
  ) {
    return "LiveKit аварийно разорвал соединение, поэтому клиент автоматически отправил rageshake.";
  }

  if (incident.reason.startsWith("element_call_error:")) {
    const errorCode =
      typeof incident.details?.errorCode === "string"
        ? incident.details.errorCode
        : incident.reason.replace("element_call_error:", "");

    return `Код обработал типизированную ошибку ${errorCode}, но инцидент требует автоматической отправки логов.`;
  }

  if (incident.classification === "handled") {
    return `Код обработал кейс ${incident.reason}, но он считается диагностически важным и поэтому логи отправлены автоматически.`;
  }

  return `Произошла необработанная ошибка ${incident.reason}, поэтому логи отправлены автоматически.`;
}

function buildIncidentLabel(incident: CallRageshakeIncident): string {
  const sanitizedReason = incident.reason
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return `call_auto_${incident.classification}_${sanitizedReason || "unknown"}`;
}

export function buildCallRageshakeDescription(
  roomId: string,
  incident: CallRageshakeIncident,
  createdAtMs = Date.now(),
): string {
  const createdAt = new Date(createdAtMs).toISOString();
  const trigger = incident.trigger ?? buildDefaultIncidentTrigger(incident);
  const metadata = JSON.stringify(
    {
      classification: incident.classification,
      reason: incident.reason,
      trigger,
      screen: incident.screen,
      roomId,
      createdAt,
    },
    detailsReplacer,
  );
  const lines = [
    `${AUTO_RAGESHAKE_METADATA_PREFIX} ${metadata}`,
    "",
    "Автоматическая отправка rageshake.",
    `Почему отправили: ${trigger}`,
    `Класс причины: ${formatClassification(incident.classification)}`,
    `Код причины: ${incident.reason}`,
    `Экран: ${incident.screen}`,
    `Комната: ${roomId}`,
    `Время: ${createdAt}`,
  ];

  if (incident.details && Object.keys(incident.details).length > 0) {
    lines.push("");
    lines.push("Details:");
    lines.push(JSON.stringify(incident.details, detailsReplacer, 2));
  }

  return lines.join("\n");
}

export function classifyCallRageshakeError(
  error: unknown,
): ClassifiedCallError | null {
  if (error instanceof ElementCallError) {
    if (error.code === ErrorCode.CONNECTION_LOST_ERROR) {
      return null;
    }

    const baseDetails = {
      errorCode: error.code,
      errorCategory: error.category,
      errorTitle: error.localisedTitle,
      errorMessage: error.localisedMessage,
      ...normalizeUnknownError(error),
    };

    if (error.code === ErrorCode.UNKNOWN_ERROR) {
      return {
        classification: "unhandled",
        reason: "element_call_unknown_error",
        trigger:
          "Клиент дошёл до UNKNOWN_ERROR, то есть произошла необработанная ошибка без отдельного сценария восстановления.",
        details: baseDetails,
      };
    }

    return {
      classification: "handled",
      reason: `element_call_error:${error.code}`,
      trigger: `Код обработал ошибку ${error.code} и показал штатный сценарий, но этот кейс отправлен в rageshake для диагностики.`,
      details: baseDetails,
    };
  }

  return {
    classification: "unhandled",
    reason: "unhandled_exception",
    trigger:
      "Произошло необработанное исключение вне типизированных ошибок Element Call.",
    details: normalizeUnknownError(error),
  };
}

export class ReconnectWindowTracker {
  private readonly timestamps: number[] = [];
  private thresholdActive = false;

  public constructor(
    private readonly threshold = RECONNECT_THRESHOLD,
    private readonly windowMs = RECONNECT_WINDOW_MS,
  ) {}

  private prune(nowMs: number): void {
    while (
      this.timestamps.length > 0 &&
      nowMs - this.timestamps[0] > this.windowMs
    ) {
      this.timestamps.shift();
    }
  }

  public register(nowMs: number): {
    countInWindow: number;
    thresholdReached: boolean;
  } {
    this.prune(nowMs);

    if (this.timestamps.length < this.threshold) {
      this.thresholdActive = false;
    }

    this.timestamps.push(nowMs);
    this.prune(nowMs);

    const countInWindow = this.timestamps.length;
    const thresholdReached =
      countInWindow >= this.threshold && !this.thresholdActive;

    if (thresholdReached) {
      this.thresholdActive = true;
    }

    return { countInWindow, thresholdReached };
  }
}

export interface CallAutoRageshakeApi {
  reportIncident: (incident: CallRageshakeIncident) => void;
  reportError: (error: unknown, context: CallRageshakeErrorContext) => void;
  recordReconnect: (
    context: Omit<CallRageshakeIncident, "classification" | "reason">,
  ) => void;
}

export function useCallAutoRageshake(roomId: string): CallAutoRageshakeApi {
  const { submitRageshake, available } = useSubmitRageshake();
  const reconnectTrackerRef = useRef(new ReconnectWindowTracker());
  const lastReportedAtRef = useRef(new Map<string, number>());

  const reportIncident = useCallback(
    (incident: CallRageshakeIncident): void => {
      if (!available) {
        return;
      }

      const nowMs = Date.now();
      const signature = [
        incident.classification,
        incident.reason,
        incident.screen,
      ].join("|");
      const lastReportedAt = lastReportedAtRef.current.get(signature) ?? 0;

      if (nowMs - lastReportedAt < INCIDENT_DEDUPE_MS) {
        return;
      }

      lastReportedAtRef.current.set(signature, nowMs);

      void submitRageshake({
        sendLogs: true,
        roomId,
        label: buildIncidentLabel(incident),
        description: buildCallRageshakeDescription(roomId, incident, nowMs),
      }).catch((error) => {
        logger.error(
          "[Rageshake] Не удалось автоматически отправить rageshake",
          {
            roomId,
            incidentReason: incident.reason,
            error,
          },
        );
      });
    },
    [available, roomId, submitRageshake],
  );

  const reportError = useCallback(
    (error: unknown, context: CallRageshakeErrorContext): void => {
      const classifiedError = classifyCallRageshakeError(error);
      if (!classifiedError) {
        return;
      }

      reportIncident({
        classification: classifiedError.classification,
        reason: classifiedError.reason,
        screen: context.screen,
        trigger: classifiedError.trigger,
        details: {
          ...context.details,
          ...classifiedError.details,
        },
      });
    },
    [reportIncident],
  );

  const recordReconnect = useCallback(
    ({
      screen,
      details,
    }: Omit<CallRageshakeIncident, "classification" | "reason">): void => {
      const nowMs = Date.now();
      const { countInWindow, thresholdReached } =
        reconnectTrackerRef.current.register(nowMs);

      if (!thresholdReached) {
        return;
      }

      reportIncident({
        classification: "handled",
        reason: "reconnect_threshold",
        screen,
        trigger: `За ${Math.round(
          RECONNECT_WINDOW_MS / 60_000,
        )} минут произошло не меньше ${countInWindow} реконнектов, поэтому клиент автоматически отправил rageshake.`,
        details: {
          ...details,
          reconnectCountInWindow: countInWindow,
          reconnectThreshold: RECONNECT_THRESHOLD,
          reconnectWindowMs: RECONNECT_WINDOW_MS,
        },
      });
    },
    [reportIncident],
  );

  return {
    reportIncident,
    reportError,
    recordReconnect,
  };
}
